import React, { useMemo, useState } from 'react';
import { storedSections } from '../../../../services/songSectionsModel';
import { timedLyricsOf } from '../stage/stageModel';
import { currentMarker, sectionMarkersFor } from './sectionMarkers';
import './SectionJumpRow.css';

/**
 * Jump to a section of the track (2026-09-28; sectionMarkers.js has
 * Anthony's words and how the times are found). A row under the strip's
 * transport: one button per section, the one the playhead is in highlighted,
 * each showing its time. Tapping jumps there.
 *
 * **Adjust** turns the row into "set it here": play to where a section really
 * starts, tap it, and that time is saved on the song for this track's
 * timeline (every section is offered then, including any the words could not
 * place). **Reset** on a hand-set one returns it to the automatic time.
 *
 * **Loop** (Rehearsal mode, 2026-09-28): with it on, tapping a section loops
 * it, from its marker to the next one (or the end of the track); tapping it
 * again, or turning Loop off, lets go. The player owns the loop (it owns the
 * clock), so it survives a Reference -> Track swap and ends with the track.
 *
 * Rendered only for the tab's own audio track with sections to show; a song
 * with sections but a track with no timed words shows one line saying so,
 * rather than nothing, so the feature is findable. Plain <button>s, each
 * declaring its height (native.css).
 */
const fmt = (t) => {
	const s = Math.max(0, Math.floor(Number(t) || 0));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const SectionJumpRow = ({ song, file, files = [], player, onSetMark = null }) => {
	const [adjusting, setAdjusting] = useState(false);
	const [looping, setLooping] = useState(false);
	const [error, setError] = useState('');
	const lyrics = timedLyricsOf(file, files);
	const timed = lyrics ? lyrics.timed : null;
	const markers = useMemo(() => sectionMarkersFor({ song, file, timed, files }), [song, file, timed, files]);
	const sections = (song && storedSections(song)) || [];
	if (!song || !file || !player || !sections.length) return null;

	const now = Number(player.currentTime) || 0;
	const cur = currentMarker(markers, now);
	const byId = new Map(markers.map((m) => [m.sectionId, m]));

	if (!markers.length && !adjusting) {
		return (
			<div className="ld-secmarks" role="group" aria-label="Song sections">
				<p className="ld-secmarks__note">
					{timed ? 'The sections could not be found in this track’s words.' : 'Sections appear here once this track’s words are timed.'}
				</p>
				{onSetMark && (
					<button type="button" className="ld-secmarks__tool" onClick={() => setAdjusting(true)}>
						Set by ear
					</button>
				)}
			</div>
		);
	}

	const save = async (sectionId, t) => {
		setError('');
		try {
			await onSetMark(file, sectionId, t);
		} catch (e) {
			setError(`The section time did not save. Please tell Anthony right away. (${e && e.message ? e.message : 'unknown error'})`);
		}
	};

	// Adjusting offers every section in song order; otherwise the placed ones in time order.
	const rows = adjusting ? sections.map((s) => byId.get(s.id) || { sectionId: s.id, label: s.label, t: null, nudged: false }) : markers;
	const canLoop = typeof player.setLoop === 'function';
	const activeLoop = player.loop || null;
	const loopFor = (m) => {
		const i = markers.findIndex((x) => x.sectionId === m.sectionId);
		const next = markers[i + 1];
		const end = next ? next.t : Number(player.duration) || 0;
		return end > m.t ? { start: m.t, end, sectionId: m.sectionId, label: m.label } : null;
	};
	const tap = (m) => {
		if (adjusting) return save(m.sectionId, now);
		if (m.t === null) return undefined;
		if (looping && canLoop) {
			if (activeLoop && activeLoop.sectionId === m.sectionId) return player.setLoop(null);
			return player.setLoop(loopFor(m));
		}
		return player.seek(m.t);
	};
	const toggleLooping = () => {
		if (looping && canLoop) player.setLoop(null);
		setLooping((v) => !v);
	};

	return (
		<div className={`ld-secmarks${adjusting ? ' is-adjusting' : ''}`} role="group" aria-label="Song sections">
			<span className="ld-secmarks__label">{adjusting ? 'Set here:' : looping ? 'Loop' : 'Jump to'}</span>
			<div className="ld-secmarks__list">
				{rows.map((m) => (
					<span key={m.sectionId} className="ld-secmarks__item">
						<button
							type="button"
							className={`ld-secmarks__btn${!adjusting && cur && cur.sectionId === m.sectionId ? ' is-current' : ''}${m.nudged ? ' is-nudged' : ''}${activeLoop && activeLoop.sectionId === m.sectionId ? ' is-looping' : ''}`}
							aria-pressed={!adjusting && Boolean(cur && cur.sectionId === m.sectionId)}
							onClick={() => tap(m)}>
							{activeLoop && activeLoop.sectionId === m.sectionId && <i className="fa-sharp fa-solid fa-repeat" aria-hidden="true" />}
							{m.label}
							<span className="ld-secmarks__time">{m.t === null ? 'not placed' : fmt(m.t)}</span>
						</button>
						{adjusting && m.nudged && (
							<button type="button" className="ld-secmarks__reset" onClick={() => save(m.sectionId, null)}>
								Reset
							</button>
						)}
					</span>
				))}
			</div>
			{canLoop && !adjusting && (
				<button type="button" className={`ld-secmarks__tool${looping ? ' is-on' : ''}`} aria-pressed={looping} onClick={toggleLooping}>
					<i className="fa-sharp fa-solid fa-repeat" aria-hidden="true" />
					{looping ? 'Loop on' : 'Loop'}
				</button>
			)}
			{onSetMark && !looping && (
				<button type="button" className="ld-secmarks__tool" aria-pressed={adjusting} onClick={() => setAdjusting((v) => !v)}>
					{adjusting ? 'Done' : 'Adjust'}
				</button>
			)}
			{looping && (
				<p className="ld-secmarks__note">
					{activeLoop ? `Looping ${activeLoop.label || 'this section'} (${fmt(activeLoop.start)} to ${fmt(activeLoop.end)}). Tap it again to stop.` : 'Tap a section to loop it.'}
				</p>
			)}
			{adjusting && <p className="ld-secmarks__note">Play to where a section starts, then tap it to set it there ({fmt(now)}).</p>}
			{error && (
				<div className="ld-dismissable-error" role="alert">
					<p>{error}</p>
					<button type="button" className="ld-dismiss" onClick={() => setError('')}>
						Dismiss
					</button>
				</div>
			)}
		</div>
	);
};

export default SectionJumpRow;
